import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import * as React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import { v4 as uuid } from 'uuid';
import * as yup from 'yup';
import {
  getFilteredEntities,
  getByPartitionKey,
} from '../../lib/services/api-service';
import TenantContext from '../../lib/context/TenantContext';
import { addEntity } from '../../lib/services/api-service';
import { TableName } from '../../constants/table';
import { FormInputNumber } from '../formComponent/FormInputNumber';
import { useGetDefaultLedger } from '../../lib/query/defaultLedger';
import { FormInputText } from '../formComponent/FormInputText';
import { useGetAllCompanyMasters } from '../../lib/query/companyMaster';

export interface IDefaultLedger {
  LedgerOCR: string;
  rate: number;
  LedgerName: string;
  companyId: string;
  vendorId: string;
}

interface CompanyOption {
  id: string;
  name: string;
  status: string;
}

interface VendorOption {
  id: string;
  name: string;
}

export default function AddDefaultLedger(props: {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { tenant } = React.useContext(TenantContext);
  const { refetch } = useGetDefaultLedger(tenant.id);

  // Fetch company data
  const { data: companies, isLoading: isCompaniesLoading } =
    useGetAllCompanyMasters(tenant.id);

  const [companyOptions, setCompanyOptions] = React.useState<CompanyOption[]>(
    [],
  );
  const [vendors, setVendors] = React.useState<VendorOption[]>([]);
  const [commonCodes, setCommonCodes] = React.useState<any[]>([]);

  // Fetch CommonCode (vendor) data
  React.useEffect(() => {
    const fetchCommonCodes = async () => {
      const data = await getByPartitionKey(TableName.CommonCode, tenant.id);
      setCommonCodes(data);
    };
    fetchCommonCodes();
  }, [tenant.id]);

  React.useEffect(() => {
    if (companies) {
      const uniqueCompanies = new Map();
      companies.forEach(company => {
        if (
          company.Active === true &&
          !uniqueCompanies.has(company.CompanyName)
        ) {
          uniqueCompanies.set(company.CompanyName, {
            id: company.RowKey,
            name: company.CompanyName,
            status: company.Active,
          });
        }
      });
      const options = Array.from(uniqueCompanies.values());
      setCompanyOptions(options);
    }
  }, [companies]);

  const schema = yup
    .object()
    .shape({
      LedgerOCR: yup.string().required('Ledger OCR is required'),
      rate: yup.number().required('Rate is required'),
      LedgerName: yup.string().required('Ledger Name is required'),
      companyId: yup.string().required('Company is required'),
      vendorId: yup.string().required('Vendor is required'),
    })
    .required();

  const { handleSubmit, control, reset, watch } = useForm<IDefaultLedger>({
    resolver: yupResolver(schema),
  });

  const companyId = watch('companyId');

  // Filter vendors by selected company
  React.useEffect(() => {
    if (companyId && commonCodes.length > 0) {
      const filteredVendors = commonCodes
        .filter(cc => cc.CompanyId === companyId)
        .map(cc => ({ id: cc.RowKey, name: cc.CommonCode }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setVendors(filteredVendors);
    } else {
      setVendors([]);
    }
  }, [companyId, commonCodes]);

  const onSubmit = async (data: IDefaultLedger) => {
    const existingEntries = await getFilteredEntities(
      TableName.DefaultLedger,
      tenant.id,
    );
    const isDuplicate = existingEntries.some(
      (entry: any) =>
        entry.CompanyId === data.companyId &&
        entry.VendorId === data.vendorId &&
        entry.LedgerOCR === data.LedgerOCR &&
        entry.Rate === data.rate,
    );

    if (isDuplicate) {
      toast.warn('Entry already exists.');
      return;
    }

    await addEntity(TableName.DefaultLedger, {
      partitionKey: tenant.id.toLowerCase(),
      rowKey: uuid().toLowerCase(),
      LedgerName: data.LedgerName,
      Rate: data.rate,
      LedgerOCR: data.LedgerOCR,
      CompanyId: data.companyId,
      VendorId: data.vendorId,
    })
      .then(() => {
        toast.success(
          `Successfully added new Default Ledger with name: ${data.LedgerOCR}`,
        );
        refetch();
        resetForm();
      })
      .catch(() => {
        toast.error('something went wrong');
      });
  };

  const handleClose = () => {
    resetForm();
  };

  const resetForm = () => {
    reset();
    props.setShowDialog(false);
  };

  return (
    <React.Fragment>
      <Dialog open={props.showDialog} onClose={handleClose}>
        <DialogTitle>Add Default Ledger</DialogTitle>

        <DialogContent sx={{ width: '400px' }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mt-4">
              <Controller
                name="companyId"
                control={control}
                render={({ field }) => (
                  <Box sx={{ minWidth: 234 }}>
                    <FormControl fullWidth>
                      <InputLabel id="company-label">Company</InputLabel>
                      <Select
                        {...field}
                        labelId="company-label"
                        id="company-select"
                        label="Company"
                        size="small">
                        {isCompaniesLoading ? (
                          <MenuItem disabled>Loading...</MenuItem>
                        ) : (
                          companyOptions
                            .sort((a, b) => {
                              return a.name.localeCompare(b.name);
                            })
                            .map(company => (
                              <MenuItem key={company.name} value={company.id}>
                                {company.name}
                              </MenuItem>
                            ))
                        )}
                      </Select>
                    </FormControl>
                  </Box>
                )}
              />
            </div>
            <div className="mt-4">
              <Controller
                name="vendorId"
                control={control}
                render={({ field }) => (
                  <Box sx={{ minWidth: 234 }}>
                    <FormControl fullWidth>
                      <InputLabel id="vendor-label">Vendor</InputLabel>
                      <Select
                        {...field}
                        labelId="vendor-label"
                        id="vendor-select"
                        label="Vendor"
                        size="small"
                        disabled={!companyId || vendors.length === 0}>
                        {vendors.map(vendor => (
                          <MenuItem key={vendor.id} value={vendor.id}>
                            {vendor.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
              />
            </div>
            <div className="mt-4">
              <Controller
                name="LedgerOCR"
                control={control}
                render={({ field }) => (
                  <Box sx={{ minWidth: 234 }}>
                    <FormControl fullWidth>
                      <InputLabel id="fieldName-label">Ledger OCR</InputLabel>
                      <Select
                        {...field}
                        labelId="fieldName-label"
                        id="fieldName-select"
                        label="Ledger OCR"
                        size="small">
                        <MenuItem value="IGST">IGST</MenuItem>
                        <MenuItem value="GST">GST</MenuItem>
                        <MenuItem value="SGST">SGST</MenuItem>
                        <MenuItem value="CGST">CGST</MenuItem>
                        <MenuItem value="ROUND OFF">ROUND OFF</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                )}
              />
            </div>
            <div className="mt-4">
              <FormInputNumber name="rate" control={control} label="Rate" />
            </div>
            <div className="mt-4">
              <FormInputText
                name="LedgerName"
                control={control}
                label="Ledger Name"
              />
            </div>

            <div className="mt-6 flex items-end justify-end space-x-4">
              <Button onClick={handleClose} variant="contained">
                Discard
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!companyId || vendors.length === 0}>
                Add
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
}
